import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useNuxtApp } from "nuxt/app";
export const makeTree = (nodes, pid = null) => nodes.filter((n) => n.parentId === pid).map((n) => ({
  ...n,
  items: makeTree(nodes, Number(n.id))
}));
const getCategoryParent = (parentId, categories) => categories.find((c) => c.id === parentId);
const getCategoryParents = (category, categories, parents) => {
  if (parents.length === 0) {
    parents.push(category);
  }
  if (category.parentId) {
    const parent = getCategoryParent(category.parentId, categories);
    if (parent) {
      parents.push(parent);
      return getCategoryParents(parent, categories, parents);
    }
  }
  return parents;
};
const findCategoryParent = (currentCategory, categories) => {
  if (!currentCategory?.parentId) {
    return void 0;
  }
  return (categories || []).find((t) => t.id === currentCategory?.parentId);
};
const getCategoryChildrens = (currentCategory, categories) => {
  const catalogTree = makeTree(categories, null);
  return getCategoryChildren(currentCategory, catalogTree);
};
const getCategoryChildren = (currentCategory, catalog) => {
  const queue = [...catalog];
  let result = [];
  let head = 0;
  let tail = queue.length;
  while (head < tail) {
    const t = queue[head++];
    if (t.id === currentCategory?.id) {
      result = t.items;
      break;
    }
    if (t.items) {
      queue.push(...t.items);
      tail += t.items.length;
    }
  }
  return result;
};
export const catalogStore = defineStore("catalogStore", () => {
  const { $api } = useNuxtApp();
  const currentCategory = ref(null);
  const error = ref(null);
  const categories = ref([]);
  const loading = ref(false);
  const filters = ref([]);
  const filterPages = ref([]);
  const filterInfo = ref({});
  const filterPrices = ref({});
  const getCatalog = async () => {
    try {
      loading.value = true;
      const catalogRes = await $api.categoriesApi.getCategories(9999);
      categories.value = catalogRes;
      return catalogRes;
    } finally {
      loading.value = false;
    }
  };
  const setCurrentCategory = (currentCat) => {
    currentCategory.value = currentCat;
  };
  const getFilters = async ({ slug, slugs, filtersList, basePath, canceledOptions }) => {
    const path = [basePath, slug, "filter-data"].join("/");
    const res = await $api.filterApi.getFiltersBySlug(path, slugs, filtersList, true, canceledOptions);
    filters.value = res?.options || [];
    filterPages.value = res?.filterPages || [];
    filterPrices.value = res?.prices || [];
  };
  const getFilterInfo = async ({ path }) => {
    const res = await $api.filterApi.getFilterInfo(path);
    if (res?.redirect) {
      return res;
    }
    filterInfo.value = res;
  };
  const resetFilters = () => {
    filters.value = [];
    filterPages.value = [];
    filterInfo.value = {};
    filterPrices.value = {};
  };
  const resetFilterInfo = () => {
    filterInfo.value = {};
  };
  const allCategories = computed(() => categories.value);
  const catalogTree = computed(() => makeTree(categories.value, null));
  const breadcrumbs = computed(() => {
    const breadcrumbs2 = [{ title: "\u0413\u043B\u0430\u0432\u043D\u0430\u044F", path: "/" }];
    if (currentCategory.value) {
      let path = "";
      getCategoryParents(currentCategory.value, categories.value, []).reverse().forEach(({ name: title, slug }) => {
        path += `/${slug}`;
        breadcrumbs2.push({ title, path });
      });
    }
    return breadcrumbs2;
  });
  const getCurrentCategory = computed(() => currentCategory.value);
  const categoryChildrensList = computed(() => getCategoryChildrens(currentCategory.value, categories.value));
  const categoryParentList = computed(() => findCategoryParent(currentCategory.value, categories.value));
  return {
    currentCategory,
    error,
    categories,
    loading,
    filters,
    filterPages,
    filterInfo,
    filterPrices,
    getCatalog,
    setCurrentCategory,
    getFilters,
    getFilterInfo,
    resetFilters,
    resetFilterInfo,
    allCategories,
    catalogTree,
    breadcrumbs,
    getCurrentCategory,
    categoryChildrensList,
    categoryParentList
  };
});
